import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  Loader2,
  Mail,
  Lock,
  Eye,
  EyeOff,
  Shield,
  Building2,
  KeyRound,
  Fingerprint,
  Landmark,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import {
  authService,
  magicLinkApi,
  mfaApi,
  passkeysApi,
  publicTenantAuthConfigApi,
  publicVerificationApi,
} from '../services/auth.service';
import { useAuthActions } from '@/stores';
import { toast } from 'sonner';
import { decodeJwt } from '@/lib/api-client';
import { VerificationForm } from './VerificationForm';
import { MfaBackupCodesPanel, MfaTotpSetupPanel } from './MfaTotpSetupPanel';
import {
  isMfaEnrollmentRequired,
  isMfaRequired,
  isVerificationRequired,
} from '../types/auth.types';
import { toPublicKeyCredentialRequestOptions } from '../utils/webauthn';
import type {
  LoginResponse,
  MFASetupResponse,
  MfaEnrollmentRequiredLoginResponse,
  MfaRequiredLoginResponse,
  PublicTenantAuthConfig,
  PublicVerificationStatus,
  SuperAdminLoginResponse,
  VerificationRequiredResponse,
} from '../types/auth.types';
import {
  mapLoginResponseToAuthSession,
  normalizeServerPermissions,
} from '../utils/auth-session.mapper';

type LoginMode = 'tenant' | 'superadmin';

// Validation Schema for Tenant Login
const tenantLoginSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Please enter a valid email address'),
  password: z
    .string()
    .min(1, 'Password is required')
    .min(6, 'Password must be at least 6 characters'),
  tenantSlug: z
    .string()
    .min(1, 'Company code is required')
    .regex(/^[a-z0-9-]+$/, 'Company code must be lowercase letters, numbers, or hyphens'),
});

const passkeyLoginSchema = tenantLoginSchema.pick({
  email: true,
  tenantSlug: true,
});

// Validation Schema for SuperAdmin Login
const superadminLoginSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Please enter a valid email address'),
  password: z
    .string()
    .min(1, 'Password is required')
    .min(6, 'Password must be at least 6 characters'),
});

const mfaChallengeSchema = z.object({
  code: z
    .string()
    .trim()
    .min(1, 'MFA code is required')
    .regex(/^(\d{6}|[A-Za-z0-9]{4}-[A-Za-z0-9]{4})$/, 'Enter a 6-digit code or backup code'),
});

type TenantLoginFormData = z.infer<typeof tenantLoginSchema>;
type PasskeyLoginFormData = z.infer<typeof passkeyLoginSchema>;
type SuperAdminLoginFormData = z.infer<typeof superadminLoginSchema>;
type MfaChallengeFormData = z.infer<typeof mfaChallengeSchema>;
type VerificationReturnMode = 'retry-login' | 'sign-in';
const DEFAULT_MAGIC_LINK_COOLDOWN_SECONDS = 60;
const TENANT_AUTH_CONFIG_LOOKUP_DEBOUNCE_MS = 700;
const TENANT_AUTH_CONFIG_CACHE_MS = 5 * 60 * 1000;

const DEFAULT_PUBLIC_AUTH_CONFIG: PublicTenantAuthConfig = {
  passkeys: {
    enabled: false,
  },
  magicLink: {
    enabled: false,
    expiryMinutes: 15,
  },
};

function withDefaultPublicAuthConfig(
  config: PublicTenantAuthConfig | undefined,
): PublicTenantAuthConfig {
  return {
    passkeys: {
      enabled: config?.passkeys?.enabled ?? DEFAULT_PUBLIC_AUTH_CONFIG.passkeys.enabled,
    },
    magicLink: {
      enabled: config?.magicLink?.enabled ?? DEFAULT_PUBLIC_AUTH_CONFIG.magicLink.enabled,
      expiryMinutes:
        toPositiveInteger(config?.magicLink?.expiryMinutes) ??
        DEFAULT_PUBLIC_AUTH_CONFIG.magicLink.expiryMinutes,
    },
  };
}

function normalizeTenantSlug(value: string): string {
  return value.trim().toLowerCase();
}

function toPositiveInteger(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return Math.ceil(value);
  }

  if (typeof value === 'string' && value.trim()) {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
  }

  return undefined;
}

function formatCooldown(seconds: number): string {
  const normalized = Math.max(0, Math.ceil(seconds));
  if (normalized < 60) {
    return `${normalized}s`;
  }

  const minutes = Math.floor(normalized / 60);
  const remainingSeconds = normalized % 60;
  return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
}

function getApiError(error: unknown): {
  status?: number;
  message?: string;
  retryAfterSeconds?: number;
} {
  if (typeof error !== 'object' || error === null || !('response' in error)) {
    return {};
  }

  const response = error.response as
    | {
        status?: number;
        data?: { message?: unknown; retryAfterSeconds?: unknown; retryAfter?: unknown };
        headers?: Record<string, unknown>;
      }
    | undefined;
  const headerRetryAfter = response?.headers?.['retry-after'] ?? response?.headers?.['Retry-After'];
  return {
    status: response?.status,
    message: typeof response?.data?.message === 'string' ? response.data.message : undefined,
    retryAfterSeconds: toPositiveInteger(
      response?.data?.retryAfterSeconds ?? response?.data?.retryAfter ?? headerRetryAfter,
    ),
  };
}

function isVerificationComplete(status: PublicVerificationStatus): boolean {
  return (
    status.companyEmailVerified &&
    status.emailVerified &&
    (!status.maskedPhone || status.phoneVerified)
  );
}

function toVerificationRequiredResponse(
  status: PublicVerificationStatus,
  tenantSlug: string,
): VerificationRequiredResponse {
  return {
    success: false,
    requiresVerification: true,
    message: status.maskedPhone
      ? 'Please verify your company email, admin email, and phone to continue'
      : 'Please verify your company email and admin email to continue',
    verificationStatus: status,
    tenant: { slug: tenantSlug },
  };
}

function loginHref(email: string, tenantSlug: string): string {
  const search = new URLSearchParams({ email, tenantSlug });
  return `/login?${search.toString()}`;
}

const AUTH_ASSURANCE_ITEMS: Array<{
  icon: LucideIcon;
  label: string;
  description: string;
}> = [
  {
    icon: Building2,
    label: 'Company-scoped access',
    description: 'Company code keeps each workspace isolated at sign-in.',
  },
  {
    icon: Shield,
    label: 'Privileged access control',
    description: 'Sensitive roles continue only after MFA enrollment or challenge.',
  },
  {
    icon: Fingerprint,
    label: 'Passwordless-ready',
    description: 'Passkeys and email sign-in appear when the company policy allows them.',
  },
];

function AuthBrandLockup({ className }: { className?: string }) {
  return (
    <div className={cn('flex items-center gap-3', className)}>
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-primary/15 bg-primary/10 text-primary shadow-sm">
        <span className="text-lg font-semibold">U</span>
      </div>
      <div className="min-w-0">
        <p className="text-base font-semibold text-foreground">UniTree</p>
        <p className="text-sm text-muted-foreground">HRMS identity access</p>
      </div>
    </div>
  );
}

function AuthShell({ children, wide = false }: { children: ReactNode; wide?: boolean }) {
  return (
    <main className="relative min-h-dvh overflow-hidden bg-[#f7f8fb] text-foreground dark:bg-background">
      <div className="absolute inset-x-0 top-0 flex h-1" aria-hidden="true">
        <span className="flex-1 bg-primary" />
        <span className="w-32 bg-emerald-500" />
        <span className="w-24 bg-amber-500" />
      </div>
      <div
        className={cn(
          'relative mx-auto grid min-h-dvh w-full items-center gap-8 px-4 py-6 sm:px-6 lg:px-8',
          wide
            ? 'max-w-7xl lg:grid-cols-[minmax(280px,0.62fr)_minmax(620px,760px)]'
            : 'max-w-6xl lg:grid-cols-[minmax(300px,0.85fr)_minmax(420px,480px)]',
        )}
      >
        <aside className="hidden min-h-[560px] flex-col justify-between border-r border-border/80 pr-10 lg:flex">
          <AuthBrandLockup />

          <div className="max-w-md">
            <p className="text-sm font-medium text-primary">Secure workspace sign-in</p>
            <h1 className="mt-3 text-4xl font-semibold leading-tight text-foreground">
              Access your HRMS workspace
            </h1>
            <p className="mt-4 text-base leading-7 text-muted-foreground">
              Use your company account for daily HRMS work, or platform administration for tenant
              operations.
            </p>
          </div>

          <div className="space-y-4">
            {AUTH_ASSURANCE_ITEMS.map(({ icon: Icon, label, description }) => (
              <div key={label} className="flex items-start gap-3">
                <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md border bg-background text-primary shadow-sm">
                  <Icon className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground">{label}</p>
                  <p className="mt-1 text-sm leading-6 text-muted-foreground">{description}</p>
                </div>
              </div>
            ))}
          </div>
        </aside>

        <section className={cn('mx-auto w-full', wide ? 'max-w-3xl' : 'max-w-md')}>
          <AuthBrandLockup className="mb-5 lg:hidden" />
          {children}
        </section>
      </div>
    </main>
  );
}

function AuthIcon({
  icon: Icon,
  tone = 'primary',
}: {
  icon: LucideIcon;
  tone?: 'primary' | 'success' | 'warning';
}) {
  const toneClassName =
    tone === 'success'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/70 dark:bg-emerald-950/30 dark:text-emerald-300'
      : tone === 'warning'
        ? 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/70 dark:bg-amber-950/30 dark:text-amber-300'
        : 'border-primary/15 bg-primary/10 text-primary';

  return (
    <div
      className={cn(
        'flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border',
        toneClassName,
      )}
    >
      <Icon className="h-6 w-6" />
    </div>
  );
}

function AuthCard({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <Card
      className={cn(
        'overflow-hidden rounded-lg border-border/90 bg-card shadow-[0_18px_50px_-28px_rgba(15,23,42,0.45)]',
        className,
      )}
    >
      {children}
    </Card>
  );
}

function PasswordVisibilityButton({
  visible,
  onToggle,
}: {
  visible: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={visible ? 'Hide password' : 'Show password'}
      aria-pressed={visible}
      onClick={onToggle}
      className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      {visible ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
    </button>
  );
}

export function LoginForm() {
  const navigate = useNavigate({ from: '/login' });
  const { login: setAuthState } = useAuthActions();
  const [showPassword, setShowPassword] = useState(false);
  const [loginMode, setLoginMode] = useState<LoginMode>('tenant');
  const [verificationRequired, setVerificationRequired] =
    useState<VerificationRequiredResponse | null>(null);
  const [mfaRequired, setMfaRequired] = useState<MfaRequiredLoginResponse | null>(null);
  const [mfaEnrollmentRequired, setMfaEnrollmentRequired] =
    useState<MfaEnrollmentRequiredLoginResponse | null>(null);
  const [mfaEnrollmentSetup, setMfaEnrollmentSetup] = useState<MFASetupResponse | null>(null);
  const [mfaEnrollmentVerifiedSession, setMfaEnrollmentVerifiedSession] =
    useState<LoginResponse | null>(null);
  const [mfaEnrollmentVerifiedBackupCodes, setMfaEnrollmentVerifiedBackupCodes] = useState<
    string[]
  >([]);
  const [pendingEmail, setPendingEmail] = useState<string>('');
  const [verificationReturnMode, setVerificationReturnMode] =
    useState<VerificationReturnMode>('retry-login');
  const recoveredVerificationKeyRef = useRef<string | null>(null);
  const [initialLoginSearch] = useState<{
    email: string;
    tenantSlug: string;
    verifyParam: string | null;
  }>(() => {
    const searchParams = new URLSearchParams(window.location.search);
    return {
      email: searchParams.get('email') ?? '',
      tenantSlug: normalizeTenantSlug(searchParams.get('tenantSlug') ?? ''),
      verifyParam: searchParams.get('verify'),
    };
  });
  const [tenantSlugLookup, setTenantSlugLookup] = useState<string>(initialLoginSearch.tenantSlug);
  const [magicLinkCooldownSeconds, setMagicLinkCooldownSeconds] = useState(0);

  // Tenant login form
  const tenantForm = useForm<TenantLoginFormData>({
    resolver: zodResolver(tenantLoginSchema),
    defaultValues: {
      email: initialLoginSearch.email,
      password: '',
      tenantSlug: initialLoginSearch.tenantSlug,
    },
  });
  const tenantSlugInput = useWatch({
    control: tenantForm.control,
    name: 'tenantSlug',
  });
  const activeTenantSlug = normalizeTenantSlug(tenantSlugInput ?? '');

  useEffect(() => {
    const nextTenantSlugLookup =
      loginMode === 'tenant' &&
      tenantLoginSchema.shape.tenantSlug.safeParse(activeTenantSlug).success
        ? activeTenantSlug
        : '';

    const timer = window.setTimeout(
      () => {
        setTenantSlugLookup(nextTenantSlugLookup);
      },
      nextTenantSlugLookup ? TENANT_AUTH_CONFIG_LOOKUP_DEBOUNCE_MS : 0,
    );

    return () => window.clearTimeout(timer);
  }, [activeTenantSlug, loginMode]);

  const tenantSlugForConfig =
    tenantLoginSchema.shape.tenantSlug.safeParse(tenantSlugLookup).success &&
    tenantSlugLookup === activeTenantSlug
      ? tenantSlugLookup
      : '';
  const publicAuthConfigQuery = useQuery({
    queryKey: ['public-auth-config', tenantSlugForConfig],
    queryFn: () => publicTenantAuthConfigApi.get(tenantSlugForConfig),
    enabled: loginMode === 'tenant' && Boolean(tenantSlugForConfig),
    retry: false,
    staleTime: TENANT_AUTH_CONFIG_CACHE_MS,
  });

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const email = searchParams.get('email');
    const tenantSlug = searchParams.get('tenantSlug');
    const verifyParam = searchParams.get('verify');
    const shouldRecoverVerification =
      verifyParam === '1' || verifyParam === 'true' || verifyParam === '"1"';

    if (!email && !tenantSlug) {
      return;
    }

    tenantForm.reset({
      email: email ?? '',
      password: '',
      tenantSlug: normalizeTenantSlug(tenantSlug ?? ''),
    });

    if (!email || !tenantSlug || !shouldRecoverVerification) {
      return;
    }

    const recoveryKey = `${email}:${tenantSlug}`;
    if (recoveredVerificationKeyRef.current === recoveryKey) {
      return;
    }
    recoveredVerificationKeyRef.current = recoveryKey;

    publicVerificationApi
      .getStatus(email, tenantSlug)
      .then((status) => {
        if (isVerificationComplete(status)) {
          toast.success('Verification Complete', {
            description: 'Your account is verified. Sign in to continue.',
          });
          void navigate({
            href: loginHref(email, tenantSlug),
          });
          return;
        }

        setPendingEmail(email);
        setVerificationReturnMode('sign-in');
        setVerificationRequired(toVerificationRequiredResponse(status, tenantSlug));
      })
      .catch((error: unknown) => {
        const { message } = getApiError(error);
        toast.error('Verification Recovery Failed', {
          description: message || 'Sign in again to continue account verification.',
        });
      });
  }, [navigate, tenantForm]);

  useEffect(() => {
    if (magicLinkCooldownSeconds <= 0) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setMagicLinkCooldownSeconds((current) => Math.max(0, current - 1));
    }, 1000);

    return () => window.clearTimeout(timeoutId);
  }, [magicLinkCooldownSeconds]);

  // SuperAdmin login form
  const superadminForm = useForm<SuperAdminLoginFormData>({
    resolver: zodResolver(superadminLoginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const mfaForm = useForm<MfaChallengeFormData>({
    resolver: zodResolver(mfaChallengeSchema),
    defaultValues: {
      code: '',
    },
  });
  const mfaEnrollmentForm = useForm<MfaChallengeFormData>({
    resolver: zodResolver(mfaChallengeSchema),
    defaultValues: {
      code: '',
    },
  });
  const mfaEnrollmentCode = useWatch({
    control: mfaEnrollmentForm.control,
    name: 'code',
  });

  const finishTenantLogin = (data: LoginResponse) => {
    // Decode JWT for non-authorizing org context only.
    const jwtPayload = decodeJwt(data.accessToken);
    const { user: enrichedUser, tenant: enrichedTenant } = mapLoginResponseToAuthSession(
      data,
      jwtPayload,
    );

    setAuthState(enrichedUser, enrichedTenant);
    toast.success('Welcome back!', {
      description: `Logged in as ${enrichedUser.fullName}`,
    });
    void navigate({ to: '/dashboard' });
  };

  const finishSuperAdminLogin = (data: SuperAdminLoginResponse | LoginResponse) => {
    const superadminUser = {
      id: data.user.id,
      email: data.user.email,
      firstName: data.user.firstName,
      lastName: data.user.lastName,
      fullName: `${data.user.firstName} ${data.user.lastName}`,
      role: 'SUPER_ADMIN' as const,
      permissions: normalizeServerPermissions(data.user.permissions ?? []),
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setAuthState(superadminUser, undefined);
    toast.success('Welcome back!', {
      description: `Logged in as ${superadminUser.fullName}`,
    });
    void navigate({ to: '/settings/system/tenants' });
  };

  const finishAuthenticatedLogin = (data: LoginResponse) => {
    if (data.user.role?.toUpperCase() === 'SUPER_ADMIN' && !data.tenant) {
      finishSuperAdminLogin(data);
      return;
    }
    finishTenantLogin(data);
  };

  const mfaEnrollmentSetupMutation = useMutation({
    mutationFn: (mfaEnrollmentToken: string) => mfaApi.setupEnrollment({ mfaEnrollmentToken }),
    onSuccess: (data) => {
      setMfaEnrollmentSetup(data);
    },
    onError: (error: unknown) => {
      const { status, message: backendMessage } = getApiError(error);
      toast.error(status === 429 ? 'Too Many Attempts' : 'MFA Setup Failed', {
        description:
          backendMessage || 'We could not start MFA setup. Return to sign in and try again.',
      });
    },
  });

  const mfaEnrollmentVerifyMutation = useMutation({
    mutationFn: (formData: MfaChallengeFormData) => {
      if (!mfaEnrollmentRequired) {
        throw new Error('MFA enrollment challenge is missing. Please sign in again.');
      }
      return mfaApi.verifyEnrollment({
        mfaEnrollmentToken: mfaEnrollmentRequired.mfaEnrollmentToken,
        code: formData.code.trim().toUpperCase(),
      });
    },
    onSuccess: (data) => {
      setMfaEnrollmentVerifiedSession(data);
      setMfaEnrollmentVerifiedBackupCodes(data.backupCodes ?? []);
      mfaEnrollmentForm.reset();
    },
    onError: (error: unknown) => {
      const { status, message: backendMessage } = getApiError(error);
      if (status === 429) {
        toast.error('Too Many Attempts', {
          description: backendMessage || 'Please wait before trying another MFA code.',
        });
        return;
      }
      toast.error('MFA Enrollment Failed', {
        description: backendMessage || 'Enter the current code from your authenticator app.',
      });
    },
  });

  // Tenant login mutation
  const tenantLoginMutation = useMutation({
    mutationFn: authService.login,
    onSuccess: (data) => {
      // Check if verification is required
      if (isVerificationRequired(data)) {
        // Store verification data and email for the verification form
        setVerificationRequired(data);
        setPendingEmail(tenantForm.getValues('email'));
        setVerificationReturnMode('retry-login');
        toast.info('Verification Required', {
          description: data.message,
        });
        return;
      }

      if (isMfaRequired(data)) {
        setMfaRequired(data);
        mfaForm.reset();
        toast.info('MFA Required', {
          description: data.message,
        });
        return;
      }

      if (isMfaEnrollmentRequired(data)) {
        setMfaEnrollmentRequired(data);
        setMfaEnrollmentSetup(null);
        setMfaEnrollmentVerifiedSession(null);
        setMfaEnrollmentVerifiedBackupCodes([]);
        mfaEnrollmentForm.reset();
        mfaEnrollmentSetupMutation.mutate(data.mfaEnrollmentToken);
        toast.info('MFA Setup Required', {
          description: data.message,
        });
        return;
      }

      finishTenantLogin(data);
    },
    onError: (error: unknown) => {
      const { status, message: backendMessage } = getApiError(error);

      if (status === 401) {
        if (backendMessage?.toLowerCase().includes('locked')) {
          toast.error('Account Locked', {
            description: backendMessage,
          });
        } else if (backendMessage?.toLowerCase().includes('password sign-in is disabled')) {
          toast.error('Password Sign-In Disabled', {
            description: backendMessage,
          });
        } else if (backendMessage?.toLowerCase().includes('password has expired')) {
          toast.error('Password Expired', {
            description: backendMessage,
          });
        } else if (backendMessage?.toLowerCase().includes('too many')) {
          toast.error('Too Many Attempts', {
            description:
              'Your account has been temporarily locked due to multiple failed login attempts. Please try again later.',
          });
        } else if (backendMessage?.toLowerCase().includes('ip')) {
          toast.error('Access Blocked', {
            description: 'Your IP address has been blocked. Please contact your administrator.',
          });
        } else {
          toast.error('Invalid Credentials', {
            description: 'The email or password you entered is incorrect. Please try again.',
          });
        }
      } else if (status === 400) {
        toast.error('Invalid Input', {
          description: backendMessage || 'Please check your login details and try again.',
        });
      } else if (status === 502 || status === 503) {
        toast.error('Service Unavailable', {
          description:
            'The authentication service is temporarily unavailable. Please try again in a moment.',
        });
      } else if (status === 429) {
        toast.error('Too Many Requests', {
          description:
            'You have made too many login attempts. Please wait a moment before trying again.',
        });
      } else if (!navigator.onLine) {
        toast.error('No Internet Connection', {
          description: 'Please check your network connection and try again.',
        });
      } else {
        toast.error('Login Failed', {
          description: backendMessage || 'An unexpected error occurred. Please try again.',
        });
      }
    },
  });

  const mfaVerifyMutation = useMutation({
    mutationFn: (formData: MfaChallengeFormData) => {
      if (!mfaRequired) {
        throw new Error('MFA challenge is missing. Please sign in again.');
      }
      return mfaApi.verify({
        mfaChallengeToken: mfaRequired.mfaChallengeToken,
        code: formData.code.trim().toUpperCase(),
      });
    },
    onSuccess: (data) => {
      setMfaRequired(null);
      mfaForm.reset();
      finishAuthenticatedLogin(data);
    },
    onError: (error: unknown) => {
      const { status, message: backendMessage } = getApiError(error);
      if (status === 429) {
        toast.error('Too Many Attempts', {
          description: backendMessage || 'Please wait before trying another MFA code.',
        });
        return;
      }
      toast.error('MFA Verification Failed', {
        description: backendMessage || 'Enter a valid MFA code or backup code.',
      });
    },
  });

  const passkeyLoginMutation = useMutation({
    mutationFn: async (formData: PasskeyLoginFormData) => {
      if (!window.PublicKeyCredential || !navigator.credentials?.get) {
        throw new Error('This browser does not support passkey sign-in.');
      }

      const options = await passkeysApi.getAuthenticateOptions({
        email: formData.email,
        tenantSlug: formData.tenantSlug,
      });
      const credential = await navigator.credentials.get({
        publicKey: toPublicKeyCredentialRequestOptions(options.options),
      });

      if (!credential || credential.type !== 'public-key') {
        throw new Error('Passkey sign-in was cancelled or did not complete.');
      }

      return passkeysApi.authenticate(
        credential as PublicKeyCredential,
        options.challenge,
        options.tenantId,
      );
    },
    onSuccess: (data) => {
      finishTenantLogin(data);
    },
    onError: (error: unknown) => {
      const { status, message: backendMessage } = getApiError(error);
      const fallbackMessage = error instanceof Error ? error.message : undefined;

      if (status === 400 || status === 401) {
        toast.error('Passkey Sign-In Failed', {
          description:
            backendMessage ||
            fallbackMessage ||
            'Check your email and company code, then try again.',
        });
        return;
      }

      toast.error('Passkey Sign-In Failed', {
        description:
          backendMessage || fallbackMessage || 'Your passkey could not be verified. Try again.',
      });
    },
  });

  const magicLinkMutation = useMutation({
    mutationFn: async (formData: PasskeyLoginFormData) => {
      return magicLinkApi.send({
        email: formData.email,
        tenantSlug: formData.tenantSlug,
      });
    },
    onSuccess: (data) => {
      const retryAfterSeconds =
        toPositiveInteger(data.retryAfterSeconds) ?? DEFAULT_MAGIC_LINK_COOLDOWN_SECONDS;
      setMagicLinkCooldownSeconds(retryAfterSeconds);
      toast.success('Sign-in link sent', {
        description: `Check your email for a one-time link. You can request another in ${formatCooldown(
          retryAfterSeconds,
        )}.`,
      });
    },
    onError: (error: unknown) => {
      const { status, message: backendMessage, retryAfterSeconds } = getApiError(error);

      if (status === 429) {
        const cooldownSeconds = retryAfterSeconds ?? DEFAULT_MAGIC_LINK_COOLDOWN_SECONDS;
        setMagicLinkCooldownSeconds(cooldownSeconds);
        toast.error('Too Many Requests', {
          description:
            backendMessage ||
            `Please wait ${formatCooldown(cooldownSeconds)} before requesting another sign-in link.`,
        });
        return;
      }

      toast.error('Sign-In Link Failed', {
        description: backendMessage || 'Check your email and company code, then try again.',
      });
    },
  });

  // SuperAdmin login mutation
  const superadminLoginMutation = useMutation({
    mutationFn: authService.superadminLogin,
    onSuccess: (data) => {
      if (isMfaRequired(data)) {
        setMfaRequired(data);
        mfaForm.reset();
        toast.info('MFA Required', { description: data.message });
        return;
      }

      if (isMfaEnrollmentRequired(data)) {
        setMfaEnrollmentRequired(data);
        setMfaEnrollmentSetup(null);
        setMfaEnrollmentVerifiedSession(null);
        setMfaEnrollmentVerifiedBackupCodes([]);
        mfaEnrollmentForm.reset();
        mfaEnrollmentSetupMutation.mutate(data.mfaEnrollmentToken);
        toast.info('MFA Setup Required', { description: data.message });
        return;
      }

      finishSuperAdminLogin(data);
    },
    onError: (error: unknown) => {
      const { status, message: backendMessage } = getApiError(error);

      if (status === 401) {
        if (backendMessage?.toLowerCase().includes('disabled')) {
          toast.error('Account Disabled', {
            description:
              'This platform administrator account has been disabled. Please contact system support.',
          });
        } else {
          toast.error('Invalid Credentials', {
            description: 'The email or password you entered is incorrect. Please try again.',
          });
        }
      } else if (status === 502 || status === 503) {
        toast.error('Service Unavailable', {
          description:
            'The authentication service is temporarily unavailable. Please try again in a moment.',
        });
      } else if (!navigator.onLine) {
        toast.error('No Internet Connection', {
          description: 'Please check your network connection and try again.',
        });
      } else {
        toast.error('Login Failed', {
          description: backendMessage || 'An unexpected error occurred. Please try again.',
        });
      }
    },
  });

  const onTenantSubmit = (data: TenantLoginFormData) => {
    tenantLoginMutation.mutate(data);
  };

  const onPasskeySubmit = () => {
    const formValues = tenantForm.getValues();
    const result = passkeyLoginSchema.safeParse({
      email: formValues.email,
      tenantSlug: formValues.tenantSlug,
    });

    if (!result.success) {
      for (const issue of result.error.issues) {
        const field = issue.path[0];
        if (field === 'email' || field === 'tenantSlug') {
          tenantForm.setError(field, { type: 'manual', message: issue.message });
        }
      }
      return;
    }

    tenantForm.clearErrors(['email', 'tenantSlug']);
    passkeyLoginMutation.mutate(result.data);
  };

  const onMagicLinkSubmit = () => {
    const formValues = tenantForm.getValues();
    const result = passkeyLoginSchema.safeParse({
      email: formValues.email,
      tenantSlug: formValues.tenantSlug,
    });

    if (!result.success) {
      for (const issue of result.error.issues) {
        const field = issue.path[0];
        if (field === 'email' || field === 'tenantSlug') {
          tenantForm.setError(field, { type: 'manual', message: issue.message });
        }
      }
      return;
    }

    tenantForm.clearErrors(['email', 'tenantSlug']);
    magicLinkMutation.mutate(result.data);
  };

  const onSuperadminSubmit = (data: SuperAdminLoginFormData) => {
    superadminLoginMutation.mutate(data);
  };

  const onMfaSubmit = (data: MfaChallengeFormData) => {
    mfaVerifyMutation.mutate(data);
  };

  const onMfaEnrollmentSubmit = (data: MfaChallengeFormData) => {
    mfaEnrollmentVerifyMutation.mutate(data);
  };

  const isPending =
    tenantLoginMutation.isPending ||
    superadminLoginMutation.isPending ||
    passkeyLoginMutation.isPending ||
    magicLinkMutation.isPending ||
    mfaVerifyMutation.isPending ||
    mfaEnrollmentSetupMutation.isPending ||
    mfaEnrollmentVerifyMutation.isPending;

  const handleModeChange = (value: string) => {
    setLoginMode(value as LoginMode);
    // Reset forms when switching modes
    tenantForm.reset();
    setTenantSlugLookup('');
    superadminForm.reset();
    mfaForm.reset();
    setMfaRequired(null);
    mfaEnrollmentForm.reset();
    setMfaEnrollmentRequired(null);
    setMfaEnrollmentSetup(null);
    setMagicLinkCooldownSeconds(0);
  };

  const tenantSlugRegistration = tenantForm.register('tenantSlug');
  const activePublicAuthConfig =
    activeTenantSlug && tenantSlugForConfig === activeTenantSlug
      ? withDefaultPublicAuthConfig(publicAuthConfigQuery.data)
      : DEFAULT_PUBLIC_AUTH_CONFIG;
  const showPasswordlessLoading =
    loginMode === 'tenant' &&
    Boolean(tenantSlugForConfig) &&
    publicAuthConfigQuery.isFetching &&
    !publicAuthConfigQuery.data;
  const showMagicLinkLogin = activePublicAuthConfig.magicLink.enabled;
  const showPasskeyLogin = activePublicAuthConfig.passkeys.enabled;
  const magicLinkCoolingDown = magicLinkCooldownSeconds > 0;

  // Handle verification complete - retry the already validated login credentials
  const handleVerificationComplete = () => {
    setVerificationRequired(null);
    setPendingEmail('');
    if (verificationReturnMode === 'retry-login' && tenantForm.getValues('password')) {
      tenantLoginMutation.mutate(tenantForm.getValues());
      return;
    }

    setVerificationReturnMode('retry-login');
    toast.success('Verification Complete', {
      description: 'Your account is verified. Sign in to continue.',
    });
    void navigate({
      href: loginHref(tenantForm.getValues('email'), tenantForm.getValues('tenantSlug')),
    });
  };

  // Handle back from verification form
  const handleBackFromVerification = () => {
    setVerificationRequired(null);
    setPendingEmail('');
    setVerificationReturnMode('retry-login');
    void navigate({
      href: loginHref(tenantForm.getValues('email'), tenantForm.getValues('tenantSlug')),
    });
  };

  const handleBackFromMfa = () => {
    const isPlatformAdminChallenge = !mfaRequired?.tenant;
    setMfaRequired(null);
    mfaForm.reset();
    if (isPlatformAdminChallenge) {
      superadminForm.setValue('password', '');
    } else {
      tenantForm.setValue('password', '');
    }
  };

  const handleBackFromMfaEnrollment = () => {
    const isPlatformAdminEnrollment = !mfaEnrollmentRequired?.tenant;
    setMfaEnrollmentRequired(null);
    setMfaEnrollmentSetup(null);
    setMfaEnrollmentVerifiedSession(null);
    setMfaEnrollmentVerifiedBackupCodes([]);
    mfaEnrollmentForm.reset();
    if (isPlatformAdminEnrollment) {
      superadminForm.setValue('password', '');
    } else {
      tenantForm.setValue('password', '');
    }
  };

  const finishMfaEnrollmentSignIn = () => {
    if (!mfaEnrollmentVerifiedSession) {
      return;
    }

    const verifiedSession = mfaEnrollmentVerifiedSession;
    setMfaEnrollmentRequired(null);
    setMfaEnrollmentSetup(null);
    setMfaEnrollmentVerifiedSession(null);
    setMfaEnrollmentVerifiedBackupCodes([]);
    mfaEnrollmentForm.reset();
    finishAuthenticatedLogin(verifiedSession);
  };

  // If verification is required, show verification form
  if (verificationRequired) {
    return (
      <VerificationForm
        verificationData={verificationRequired}
        email={pendingEmail}
        onVerificationComplete={handleVerificationComplete}
        onBack={handleBackFromVerification}
      />
    );
  }

  if (mfaEnrollmentRequired) {
    const enrollmentExpiresMinutes = Math.ceil(mfaEnrollmentRequired.mfaEnrollmentExpiresIn / 60);
    const enrollmentAccountEmail = mfaEnrollmentRequired.user.email;
    const isPlatformAdminEnrollment = !mfaEnrollmentRequired.tenant;
    return (
      <AuthShell wide>
        <AuthCard>
          <CardHeader className="space-y-0 border-b bg-muted/20 p-5 sm:p-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
              <AuthIcon icon={Shield} tone="primary" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-primary">Privileged access</p>
                <CardTitle className="mt-2 text-2xl font-semibold leading-tight tracking-normal">
                  {mfaEnrollmentVerifiedSession
                    ? 'Save your backup codes'
                    : 'Set up MFA to continue'}
                </CardTitle>
                <CardDescription className="mt-2 leading-6">
                  {mfaEnrollmentVerifiedSession
                    ? `${enrollmentAccountEmail} is protected by an authenticator app. Save these codes before continuing.`
                    : `${enrollmentAccountEmail} has privileged access. Scan the QR code with an authenticator app, then enter the code within ${enrollmentExpiresMinutes} minutes.`}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-5 sm:p-6">
            {mfaEnrollmentVerifiedSession ? (
              <MfaBackupCodesPanel
                accountLabel={enrollmentAccountEmail}
                backupCodes={mfaEnrollmentVerifiedBackupCodes}
                onContinue={finishMfaEnrollmentSignIn}
                continueLabel={
                  isPlatformAdminEnrollment
                    ? 'Continue to platform administration'
                    : 'Continue to dashboard'
                }
                disabled={isPending}
              />
            ) : mfaEnrollmentSetupMutation.isPending && !mfaEnrollmentSetup ? (
              <div className="flex items-center justify-center gap-3 rounded-md border border-dashed p-6 text-sm text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
                Preparing MFA setup...
              </div>
            ) : mfaEnrollmentSetup ? (
              <form
                onSubmit={mfaEnrollmentForm.handleSubmit(onMfaEnrollmentSubmit)}
                className="space-y-4"
              >
                <MfaTotpSetupPanel
                  setupDetails={mfaEnrollmentSetup}
                  accountLabel={enrollmentAccountEmail}
                  disabled={isPending}
                  codeField={
                    <div className="space-y-2">
                      <Label htmlFor="mfa-enrollment-code">Verification code</Label>
                      <Input
                        id="mfa-enrollment-code"
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        placeholder="123456"
                        {...mfaEnrollmentForm.register('code')}
                        disabled={isPending}
                      />
                      {mfaEnrollmentForm.formState.errors.code && (
                        <p className="text-sm text-destructive">
                          {mfaEnrollmentForm.formState.errors.code.message}
                        </p>
                      )}
                    </div>
                  }
                />
                <Button
                  type="submit"
                  className="w-full"
                  size="lg"
                  disabled={isPending || !mfaEnrollmentCode?.trim()}
                >
                  {mfaEnrollmentVerifyMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <Shield className="mr-2 h-5 w-5" />
                      Verify MFA and Sign In
                    </>
                  )}
                </Button>
              </form>
            ) : (
              <div className="space-y-4">
                <p className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
                  MFA setup could not be prepared. Start again or return to sign in.
                </p>
                <Button
                  type="button"
                  className="w-full"
                  onClick={() =>
                    mfaEnrollmentSetupMutation.mutate(mfaEnrollmentRequired.mfaEnrollmentToken)
                  }
                  disabled={isPending}
                >
                  Retry MFA setup
                </Button>
              </div>
            )}
            <Button
              type="button"
              variant="ghost"
              className="mt-4 w-full"
              onClick={handleBackFromMfaEnrollment}
              disabled={isPending && !mfaEnrollmentVerifiedSession}
            >
              Back to sign in
            </Button>
          </CardContent>
        </AuthCard>
      </AuthShell>
    );
  }

  if (mfaRequired) {
    return (
      <AuthShell>
        <AuthCard>
          <CardHeader className="space-y-0 border-b bg-muted/20 p-5 sm:p-6">
            <div className="flex items-start gap-4">
              <AuthIcon icon={Shield} tone="primary" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-primary">MFA challenge</p>
                <CardTitle className="mt-2 text-2xl font-semibold leading-tight tracking-normal">
                  Verify your sign-in
                </CardTitle>
                <CardDescription className="mt-2 leading-6">
                  Enter the MFA code for {mfaRequired.user.email}. This challenge expires in{' '}
                  {Math.ceil(mfaRequired.mfaChallengeExpiresIn / 60)} minutes.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-5 sm:p-6">
            <form onSubmit={mfaForm.handleSubmit(onMfaSubmit)} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="mfa-code">MFA Code</Label>
                <Input
                  id="mfa-code"
                  type="text"
                  autoComplete="one-time-code"
                  placeholder="123456"
                  {...mfaForm.register('code')}
                  disabled={isPending}
                />
                {mfaForm.formState.errors.code && (
                  <p className="text-sm text-destructive">
                    {mfaForm.formState.errors.code.message}
                  </p>
                )}
              </div>

              <Button type="submit" className="w-full" size="lg" disabled={isPending}>
                {mfaVerifyMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <Shield className="mr-2 h-5 w-5" />
                    Verify and Sign In
                  </>
                )}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={handleBackFromMfa}
                disabled={isPending}
              >
                Back to sign in
              </Button>
            </form>
          </CardContent>
        </AuthCard>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <AuthCard>
        <CardHeader className="space-y-0 border-b bg-muted/20 p-5 sm:p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-sm font-medium text-primary">Sign in</p>
              <CardTitle className="mt-2 text-2xl font-semibold leading-tight tracking-normal">
                Welcome back
              </CardTitle>
              <CardDescription className="mt-2 leading-6">
                Continue to your UniTree workspace.
              </CardDescription>
            </div>
            <AuthIcon icon={Landmark} tone="primary" />
          </div>
        </CardHeader>
        <CardContent className="p-5 sm:p-6">
          {/* Login Mode Tabs */}
          <Tabs value={loginMode} onValueChange={handleModeChange} className="mb-6">
            <TabsList className="grid h-auto w-full grid-cols-2 rounded-md p-1">
              <TabsTrigger
                value="tenant"
                className="min-h-10 items-center gap-2 whitespace-nowrap px-2 text-xs data-[state=active]:bg-background sm:text-sm"
              >
                <Building2 className="h-4 w-4" />
                Company account
              </TabsTrigger>
              <TabsTrigger
                value="superadmin"
                className="min-h-10 items-center gap-2 whitespace-nowrap px-2 text-xs data-[state=active]:bg-background sm:text-sm"
              >
                <Shield className="h-4 w-4" />
                Platform admin
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Tenant Login Form */}
          {loginMode === 'tenant' && (
            <form onSubmit={tenantForm.handleSubmit(onTenantSubmit)} className="space-y-4">
              {/* Email Field */}
              <div className="space-y-2">
                <Label htmlFor="tenant-email">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="tenant-email"
                    type="email"
                    autoComplete="email"
                    placeholder="you@company.com"
                    className="pl-10"
                    {...tenantForm.register('email')}
                    disabled={isPending}
                  />
                </div>
                {tenantForm.formState.errors.email && (
                  <p className="text-sm text-destructive">
                    {tenantForm.formState.errors.email.message}
                  </p>
                )}
              </div>

              {/* Password Field */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="tenant-password">Password</Label>
                  <Link
                    to="/forgot-password"
                    className="text-sm font-medium text-primary hover:text-primary/80"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="tenant-password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    className="pl-10 pr-10"
                    {...tenantForm.register('password')}
                    disabled={isPending}
                  />
                  <PasswordVisibilityButton
                    visible={showPassword}
                    onToggle={() => setShowPassword(!showPassword)}
                  />
                </div>
                {tenantForm.formState.errors.password && (
                  <p className="text-sm text-destructive">
                    {tenantForm.formState.errors.password.message}
                  </p>
                )}
              </div>

              {/* Tenant Slug Field */}
              <div className="space-y-2">
                <Label htmlFor="tenantSlug">Company Code</Label>
                <Input
                  id="tenantSlug"
                  type="text"
                  autoComplete="organization"
                  placeholder="company-code"
                  {...tenantSlugRegistration}
                  onBlur={tenantSlugRegistration.onBlur}
                  onChange={(event) => {
                    const normalized = normalizeTenantSlug(event.target.value);
                    event.target.value = normalized;
                    tenantSlugRegistration.onChange(event);
                  }}
                  disabled={isPending}
                />
                <p className="text-xs text-muted-foreground">
                  Use the company code shared by your administrator.
                </p>
                {tenantForm.formState.errors.tenantSlug && (
                  <p className="text-sm text-destructive">
                    {tenantForm.formState.errors.tenantSlug.message}
                  </p>
                )}
              </div>

              {/* Submit Button */}
              <Button type="submit" className="w-full" size="lg" disabled={isPending}>
                {tenantLoginMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>

              {showPasswordlessLoading ? (
                <Button type="button" variant="outline" className="w-full" size="lg" disabled>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Checking sign-in options...
                </Button>
              ) : showMagicLinkLogin || showPasskeyLogin ? (
                <div className="grid gap-3">
                  <div className="relative py-1">
                    <div className="absolute inset-0 flex items-center" aria-hidden="true">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs">
                      <span className="bg-card px-2 text-muted-foreground">
                        Other sign-in options
                      </span>
                    </div>
                  </div>
                  {showMagicLinkLogin ? (
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      size="lg"
                      disabled={isPending || magicLinkCoolingDown}
                      onClick={onMagicLinkSubmit}
                    >
                      {magicLinkMutation.isPending ? (
                        <>
                          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                          Sending link...
                        </>
                      ) : magicLinkCoolingDown ? (
                        <>
                          <Mail className="mr-2 h-5 w-5" />
                          Try again in {formatCooldown(magicLinkCooldownSeconds)}
                        </>
                      ) : (
                        <>
                          <Mail className="mr-2 h-5 w-5" />
                          Email me a sign-in link
                        </>
                      )}
                    </Button>
                  ) : null}

                  {showPasskeyLogin ? (
                    <div className="space-y-2">
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full"
                        size="lg"
                        disabled={isPending}
                        onClick={onPasskeySubmit}
                      >
                        {passkeyLoginMutation.isPending ? (
                          <>
                            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                            Checking passkey...
                          </>
                        ) : (
                          <>
                            <KeyRound className="mr-2 h-5 w-5" />
                            Sign in with passkey
                          </>
                        )}
                      </Button>
                      <p className="text-center text-xs text-muted-foreground">
                        Use a saved passkey from this device, password manager, phone, or security
                        key.
                      </p>
                    </div>
                  ) : null}
                  {showMagicLinkLogin && magicLinkCoolingDown ? (
                    <p role="status" className="text-xs text-muted-foreground">
                      Sign-in link request accepted. Another request unlocks in{' '}
                      {formatCooldown(magicLinkCooldownSeconds)}.
                    </p>
                  ) : null}
                </div>
              ) : null}
            </form>
          )}

          {/* SuperAdmin Login Form */}
          {loginMode === 'superadmin' && (
            <form onSubmit={superadminForm.handleSubmit(onSuperadminSubmit)} className="space-y-4">
              {/* Admin Notice */}
              <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 dark:border-amber-900/70 dark:bg-amber-950/30">
                <p className="text-sm leading-6 text-amber-900 dark:text-amber-100">
                  Platform administration access. Company users should use Company account.
                </p>
              </div>

              {/* Email Field */}
              <div className="space-y-2">
                <Label htmlFor="superadmin-email">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="superadmin-email"
                    type="email"
                    autoComplete="email"
                    placeholder="admin@unitree.com"
                    className="pl-10"
                    {...superadminForm.register('email')}
                    disabled={isPending}
                  />
                </div>
                {superadminForm.formState.errors.email && (
                  <p className="text-sm text-destructive">
                    {superadminForm.formState.errors.email.message}
                  </p>
                )}
              </div>

              {/* Password Field */}
              <div className="space-y-2">
                <Label htmlFor="superadmin-password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="superadmin-password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    className="pl-10 pr-10"
                    {...superadminForm.register('password')}
                    disabled={isPending}
                  />
                  <PasswordVisibilityButton
                    visible={showPassword}
                    onToggle={() => setShowPassword(!showPassword)}
                  />
                </div>
                {superadminForm.formState.errors.password && (
                  <p className="text-sm text-destructive">
                    {superadminForm.formState.errors.password.message}
                  </p>
                )}
              </div>

              {/* Submit Button */}
              <Button type="submit" className="w-full" size="lg" disabled={isPending}>
                {superadminLoginMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  <>
                    <Shield className="mr-2 h-5 w-5" />
                    Sign in to platform admin
                  </>
                )}
              </Button>
            </form>
          )}

          {/* Sign Up Link - Only show for Tenant mode */}
          {loginMode === 'tenant' && (
            <div className="mt-6 text-center text-sm">
              <span className="text-muted-foreground">Don't have an account? </span>
              <Link to="/register" className="font-medium text-primary hover:text-primary/80">
                Sign up for free
              </Link>
            </div>
          )}
        </CardContent>
      </AuthCard>
    </AuthShell>
  );
}
